import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { THEME } from '../constants/theme';
import { useAnalytics } from '../hooks/useAnalytics';

export default function ChangePassword() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { logEvent } = useAnalytics();
  const searchParams = new URLSearchParams(location.search);
  const redirectUri = searchParams.get('redirect_uri');

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (formData.newPassword !== formData.confirmPassword) {
      setError("New passwords don't match");
      return;
    }

    if (formData.newPassword.length < 6) {
      setError("New password must be at least 6 characters long");
      return;
    }

    setLoading(true);

    try {
      logEvent('User', 'Password Change Attempt');

      // First verify the current password by trying to sign in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: supabase.auth.user()?.email,
        password: formData.currentPassword,
      });

      if (signInError) {
        throw new Error('Current password is incorrect');
      }

      // Update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: formData.newPassword
      });

      if (updateError) throw updateError;

      logEvent('User', 'Password Change Success');

      // Handle redirect back to mobile app or navigate to profile
      if (redirectUri) {
        window.location.replace(`${redirectUri}?success=true`);
      } else {
        navigate('/profile');
      }
    } catch (err) {
      setError(err.message);
      logEvent('User', 'Password Change Error', err.message);
      
      if (redirectUri) {
        window.location.replace(`${redirectUri}?error=true&error_description=${encodeURIComponent(err.message)}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white relative overflow-hidden">
      {/* Background Circles */}
      <div className="absolute left-0 bottom-0 w-96 h-96 bg-[#E5F5F5] rounded-full -translate-x-1/2 translate-y-1/2" />
      <div className="absolute right-0 top-0 w-96 h-96 bg-[#E5F5F5] rounded-full translate-x-1/2 -translate-y-1/2" />
      <div className="absolute right-0 top-0 w-16 h-16 text-[#E5F5F5]">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.0001 1H13.0001V4H11.0001V1ZM15.0001 2H17.0001V5H15.0001V2ZM7.00006 3H9.00006V6H7.00006V3ZM20.0001 4H22.0001V7H20.0001V4ZM3.00006 5H5.00006V8H3.00006V5Z" />
        </svg>
      </div>

      <div className="w-full max-w-md px-6 relative z-10">
        {/* Logo */}
        <div className="flex justify-center mb-12">
          <img src="/logo.png" alt="Aided" className="h-12" />
        </div>

        {/* Change Password Form */}
        <div className="bg-[#004D4D] rounded-2xl p-8 shadow-lg">
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-full p-4">
              <svg className="w-8 h-8 text-[#004D4D]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6-5c1.66 0 3 1.34 3 3v2H9V6c0-1.66 1.34-3 3-3zm6 17H6V10h12v10z" />
              </svg>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <input
                type="password"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                placeholder="Current Password"
                className="w-full bg-[#006666] text-white placeholder-[#99CCCC] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#99CCCC]"
                required
              />
            </div>

            <div>
              <input
                type="password"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                placeholder="New Password"
                className="w-full bg-[#006666] text-white placeholder-[#99CCCC] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#99CCCC]"
                required
              />
            </div>

            <div>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm New Password"
                className="w-full bg-[#006666] text-white placeholder-[#99CCCC] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#99CCCC]"
                required
              />
            </div>

            {error && (
              <div className="text-red-400 text-sm text-center">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#66B2B2] hover:bg-[#99CCCC] text-white rounded-lg px-4 py-3 font-medium transition-colors relative"
            >
              {loading ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                'Change Password'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
} 